import axios from 'axios';
import { useInfiniteQuery } from '@tanstack/react-query';
import endpoints from '../../data-layer/endpoints';

declare const FLICKR_API_KEY: string;

export const useGetFlickrSearch = (query: string, searchProperty: 'text' | 'tags') => {
  const queryKey = [endpoints.GetFlickrSearch.endpoint, query, searchProperty] as const;

  const queryResult = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const options = {
        page: pageParam,
        extras: 'url_o, url_q',
        content_type: 1,
        sort: 'relevance',
        license: 10,
        api_key: FLICKR_API_KEY,
        method: 'flickr.photos.search',
        format: 'json',
        nojsoncallback: 1,
        [searchProperty]: query,
      };

      const response = await axios.get<FlickrSearchResponse>(endpoints.GetFlickrSearch.endpoint, {
        params: options,
      });

      return response.data;
    },
    enabled: !!query,
    getNextPageParam: (lastPage: FlickrSearchResponse) => {
      const { page, pages } = lastPage.photos;

      // If current page is less than total pages, return next page number
      return page < pages ? page + 1 : undefined;
    },
    staleTime: Infinity,
    retry: false,
    select: (data: { pages: FlickrSearchResponse[]; pageParams: number[] }) => ({
      pages: data.pages.map(
        (page) =>
          page.photos?.photo
            ?.filter((photo: FlickrPhoto) => photo.url_o && photo.url_q)
            .map((photo: FlickrPhoto) => ({
              id: photo.id,
              title: photo.title,
              thumbUrl: photo.url_q,
              downloadUrl: photo.url_o,
              type: 'image' as const,
              isGif: false,
            })) || []
      ),
      pageParams: data.pageParams,
    }),
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
};
